import React from 'react';
import { Link } from 'react-router-dom';
import {
    Shield,
    Users,
    Award,
    Target,
    CheckCircle2,
    Building2,
    Cpu,
    HeartHandshake,
    ArrowRight,
    Globe,
    Zap,
    Clock
} from 'lucide-react';
import CountUp from 'react-countup';
import PageLayout from '../../components/Layout/PageLayout';
import PageHeader from '../../components/PageHeader/PageHeader';
import ModernCTA from '../../components/CTA/ModernCTA';
import './ModernAboutPage.scss';

const ModernAboutPage = () => {
    const stats = [
        { number: 500, suffix: '+', label: 'Clients Served', icon: <Users size={24} /> },
        { number: 15, suffix: '+', label: 'Years Experience', icon: <Clock size={24} /> },
        { number: 50, suffix: '+', label: 'Partners', icon: <HeartHandshake size={24} /> },
        { number: 99, suffix: '%', label: 'Satisfaction', icon: <Award size={24} /> },
    ];

    const values = [
        {
            icon: <Shield size={32} />,
            title: 'Trust & Integrity',
            description: 'We build lasting relationships through honest communication and ethical business practices.'
        },
        {
            icon: <Target size={32} />,
            title: 'Customer Focus',
            description: 'Your success is our priority. We tailor solutions to meet your unique requirements.'
        },
        {
            icon: <Zap size={32} />,
            title: 'Innovation',
            description: 'We stay ahead of technology trends to deliver cutting-edge solutions.'
        },
        {
            icon: <Award size={32} />,
            title: 'Excellence',
            description: 'We are committed to delivering the highest quality in everything we do.'
        }
    ];

    const capabilities = [
        'IT Hardware & Software',
        'Professional Services',
        'Office & Industrial Supplies',
        'Healthcare Products',
        'Cybersecurity Solutions',
        'Cloud Infrastructure'
    ];

    const agencies = [
        'U.S. Navy',
        'U.S. Army',
        'U.S. Air Force',
        'NASA',
        'Department of Treasury',
        'Department of Veterans Affairs'
    ];

    return (
        <PageLayout>
            <PageHeader
                title="About Jaak Tech"
                subtitle="Trusted IT Solutions Provider for Government Agencies Since 2020"
                breadcrumbs={[{ label: 'About Us' }]}
            />

            {/* Story Section */}
            <section className="jk-about-story">
                <div className="jk-about-story__container">
                    <div className="jk-about-story__grid">
                        <div className="jk-about-story__content">
                            <span className="jk-section-label">
                                <Building2 size={16} />
                                Our Story
                            </span>
                            <h2 className="jk-about-story__title">
                                Building Technology
                                <span className="highlight"> Partnerships</span> That Last
                            </h2>
                            <p className="jk-about-story__text">
                                Founded in 2020, <strong>Jaak Tech</strong> is a trusted and rapidly growing
                                IT solutions provider serving a wide range of Federal, State, Local, and Municipal
                                government agencies—including the U.S. Navy, Army, Air Force, Department of the
                                Treasury, Department of Veterans Affairs, and NASA.
                            </p>
                            <p className="jk-about-story__text">
                                With diversified capabilities across <strong>IT hardware and software, professional
                                services, office and industrial supplies, and healthcare products</strong>, Jaak
                                Technologies is uniquely positioned to meet the evolving needs of government clients.
                            </p>
                            <p className="jk-about-story__text">
                                We partner with industry-leading manufacturers and leverage a vast network of suppliers,
                                distributors, and solution providers to deliver <strong>best-in-class products, services,</strong>
                                and support.
                            </p>

                            <div className="jk-about-story__agencies">
                                <span className="jk-about-story__agencies-label">Trusted By:</span>
                                <div className="jk-about-story__agencies-list">
                                    {agencies.map((agency, index) => (
                                        <span key={index} className="jk-about-story__agency-badge">
                                            {agency}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="jk-about-story__visual">
                            <div className="jk-about-story__stats-grid">
                                {stats.map((stat, index) => (
                                    <div key={index} className="jk-about-story__stat-card">
                                        <div className="jk-about-story__stat-icon">
                                            {stat.icon}
                                        </div>
                                        <span className="jk-about-story__stat-number">
                                            <CountUp end={stat.number} duration={2.5} enableScrollSpy scrollSpyOnce />
                                            {stat.suffix}
                                        </span>
                                        <span className="jk-about-story__stat-label">{stat.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Mission Section */}
            <section className="jk-about-mission">
                <div className="jk-about-mission__container">
                    <div className="jk-about-mission__grid">
                        <div className="jk-about-mission__card jk-about-mission__card--mission">
                            <div className="jk-about-mission__icon">
                                <Target size={40} />
                            </div>
                            <h3 className="jk-about-mission__card-title">Our Mission</h3>
                            <p className="jk-about-mission__card-text">
                                To simplify technology procurement and implementation for government agencies
                                and businesses, delivering innovative solutions that drive efficiency,
                                security, and success.
                            </p>
                        </div>

                        <div className="jk-about-mission__card jk-about-mission__card--vision">
                            <div className="jk-about-mission__icon">
                                <Globe size={40} />
                            </div>
                            <h3 className="jk-about-mission__card-title">Our Vision</h3>
                            <p className="jk-about-mission__card-text">
                                To be the most trusted technology partner for organizations seeking
                                reliable, compliant, and innovative IT solutions that empower them
                                to achieve their goals.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Values Section */}
            <section className="jk-about-values">
                <div className="jk-about-values__container">
                    <div className="jk-about-values__header">
                        <span className="jk-section-label">
                            <HeartHandshake size={16} />
                            Our Values
                        </span>
                        <h2 className="jk-about-values__title">
                            What Drives Us <span className="highlight">Forward</span>
                        </h2>
                        <p className="jk-about-values__subtitle">
                            Our core values guide every decision we make and every relationship we build.
                        </p>
                    </div>

                    <div className="jk-about-values__grid">
                        {values.map((value, index) => (
                            <div key={index} className="jk-about-values__card">
                                <div className="jk-about-values__card-icon">
                                    {value.icon}
                                </div>
                                <h3 className="jk-about-values__card-title">{value.title}</h3>
                                <p className="jk-about-values__card-text">{value.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Why Choose Us Section */}
            <section className="jk-about-why">
                <div className="jk-about-why__container">
                    <div className="jk-about-why__grid">
                        <div className="jk-about-why__content">
                            <span className="jk-section-label">
                                <CheckCircle2 size={16} />
                                Why Choose Us
                            </span>
                            <h2 className="jk-about-why__title">
                                Why Choose
                                <span className="highlight"> Jaak Tech?</span>
                            </h2>

                            <ul className="jk-about-why__list">
                                <li className="jk-about-why__item">
                                    <CheckCircle2 size={22} className="jk-about-why__item-icon" />
                                    <span>Strong partnerships with leading manufacturers</span>
                                </li>
                                <li className="jk-about-why__item">
                                    <CheckCircle2 size={22} className="jk-about-why__item-icon" />
                                    <span>Highly qualified and responsive Account Managers</span>
                                </li>
                                <li className="jk-about-why__item">
                                    <CheckCircle2 size={22} className="jk-about-why__item-icon" />
                                    <span>Wide supplier and distribution network</span>
                                </li>
                                <li className="jk-about-why__item">
                                    <CheckCircle2 size={22} className="jk-about-why__item-icon" />
                                    <span>In-house technical expertise and ongoing support</span>
                                </li>
                                <li className="jk-about-why__item">
                                    <CheckCircle2 size={22} className="jk-about-why__item-icon" />
                                    <span>Scalable, compliant, and innovative solutions</span>
                                </li>
                                <li className="jk-about-why__item">
                                    <CheckCircle2 size={22} className="jk-about-why__item-icon" />
                                    <span>Government-ready procurement expertise</span>
                                </li>
                            </ul>

                            <Link to="/contact" className="jk-btn jk-btn--primary">
                                Get In Touch
                                <ArrowRight size={20} />
                            </Link>
                        </div>

                        <div className="jk-about-why__capabilities">
                            <h3 className="jk-about-why__capabilities-title">Our Capabilities</h3>
                            <div className="jk-about-why__capabilities-grid">
                                {capabilities.map((capability, index) => (
                                    <div key={index} className="jk-about-why__capability">
                                        <Cpu size={20} />
                                        <span>{capability}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <ModernCTA />
        </PageLayout>
    );
};

export default ModernAboutPage;
